import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchOlimpiadaConteudoAluno,
  fetchOlimpiadaConteudoState,
  saveOlimpiadaConteudoErrors,
  saveOlimpiadaConteudoProgress,
  saveOlimpiadaConteudoStructure,
  createOlimpiadaCategoria,
  updateOlimpiadaCategoria as updateOlimpiadaCategoriaApi,
  deleteOlimpiadaCategoria,
  restoreOlimpiadaCategoria as restoreOlimpiadaCategoriaApi,
  createOlimpiadaCurso,
  updateOlimpiadaCurso as updateOlimpiadaCursoApi,
  deleteOlimpiadaCurso,
  restoreOlimpiadaCurso as restoreOlimpiadaCursoApi,
  createOlimpiadaModulo,
  updateOlimpiadaModulo as updateOlimpiadaModuloApi,
  deleteOlimpiadaModulo,
  restoreOlimpiadaModulo as restoreOlimpiadaModuloApi,
  createOlimpiadaAula,
  updateOlimpiadaAula as updateOlimpiadaAulaApi,
  deleteOlimpiadaAula,
  restoreOlimpiadaAula as restoreOlimpiadaAulaApi,
} from "../services/olimpiadaConteudoApi";
import { normalizeConteudosData } from "../data";

const serialize = (value) => JSON.stringify(value ?? null);

const EMPTY_STATE = normalizeConteudosData({
  categorias: [],
  cursos: [],
  modulos: [],
  aulas: [],
  erros: [],
  progresso: {},
});

/**
 * Hook que gerencia o estado de Olimpiada-Conteudo.
 * Espelha o comportamento do conteudos no useAppShellState, mas independente.
 */
export function useOlimpiadaConteudoState({
  adminLogged,
  currentStudent,
  view,
  adminAba,
}) {
  const [olimpiadaConteudo, setOlimpiadaConteudoRaw] = useState(EMPTY_STATE);
  const [olimpiadaConteudoNavigationTarget, setOlimpiadaConteudoNavigationTarget] = useState(null);

  const loadedRef = useRef(false);
  const apiInitializedRef = useRef(false);
  const bootstrapRef = useRef(false);
  const skipSyncRef = useRef(true);
  const syncTimerRef = useRef(null);
  const enterRefreshRef = useRef("");
  const previousRef = useRef(EMPTY_STATE);

  const setOlimpiadaConteudo = useCallback((updater) => {
    setOlimpiadaConteudoRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return normalizeConteudosData(next || EMPTY_STATE);
    });
  }, []);

  const addOlimpiadaCategoria = useCallback(async (payload) => {
    const categoria = await createOlimpiadaCategoria(payload);
    if (!categoria) throw new Error("Falha ao criar categoria");

    setOlimpiadaConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        categorias: [...(prev.categorias || []), categoria],
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return categoria;
  }, []);

  const updateOlimpiadaCategoria = useCallback(async (categoriaId, payload) => {
    const categoria = await updateOlimpiadaCategoriaApi(categoriaId, payload);
    if (!categoria) throw new Error("Falha ao atualizar categoria");

    setOlimpiadaConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        categorias: (prev.categorias || []).map((item) =>
          item.id === categoriaId ? categoria : item,
        ),
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return categoria;
  }, []);

  const removeOlimpiadaCategoria = useCallback(async (categoriaId) => {
    await deleteOlimpiadaCategoria(categoriaId);

    setOlimpiadaConteudoRaw((prev) => {
      const cursosIds = (prev.cursos || [])
        .filter((item) => item.cat_id === categoriaId)
        .map((item) => item.id);
      const modulosIds = (prev.modulos || [])
        .filter((item) => cursosIds.includes(item.curso_id))
        .map((item) => item.id);
      const aulasIds = (prev.aulas || [])
        .filter((item) => modulosIds.includes(item.modulo_id))
        .map((item) => item.id);

      const nextValue = normalizeConteudosData({
        ...prev,
        categorias: (prev.categorias || []).filter(
          (item) => item.id !== categoriaId,
        ),
        cursos: (prev.cursos || []).filter(
          (item) => item.cat_id !== categoriaId,
        ),
        modulos: (prev.modulos || []).filter(
          (item) => !cursosIds.includes(item.curso_id),
        ),
        aulas: (prev.aulas || []).filter(
          (item) => !modulosIds.includes(item.modulo_id),
        ),
        progresso: Object.keys(prev.progresso || {}).reduce(
          (acc, studentKey) => {
            const currentProgress = { ...(prev.progresso?.[studentKey] || {}) };
            aulasIds.forEach((aulaId) => delete currentProgress[aulaId]);
            acc[studentKey] = currentProgress;
            return acc;
          },
          {},
        ),
      });

      previousRef.current = nextValue;
      return nextValue;
    });
  }, []);

  const restoreOlimpiadaCategoria = useCallback(async (categoriaId) => {
    await restoreOlimpiadaCategoriaApi(categoriaId);

    const { snapshot } = await fetchOlimpiadaConteudoState();
    const normalized = normalizeConteudosData(snapshot);
    skipSyncRef.current = true;
    previousRef.current = normalized;
    setOlimpiadaConteudoRaw(normalized);
  }, []);

  const addOlimpiadaCurso = useCallback(async (payload) => {
    const curso = await createOlimpiadaCurso(payload);
    if (!curso) throw new Error("Falha ao criar curso");

    setOlimpiadaConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        cursos: [...(prev.cursos || []), curso],
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return curso;
  }, []);

  const updateOlimpiadaCurso = useCallback(async (cursoId, payload) => {
    const curso = await updateOlimpiadaCursoApi(cursoId, payload);
    if (!curso) throw new Error("Falha ao atualizar curso");

    setOlimpiadaConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        cursos: (prev.cursos || []).map((item) =>
          item.id === cursoId ? curso : item,
        ),
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return curso;
  }, []);

  const removeOlimpiadaCurso = useCallback(async (cursoId) => {
    await deleteOlimpiadaCurso(cursoId);

    setOlimpiadaConteudoRaw((prev) => {
      const modulosIds = (prev.modulos || [])
        .filter((item) => item.curso_id === cursoId)
        .map((item) => item.id);
      const aulasIds = (prev.aulas || [])
        .filter((item) => modulosIds.includes(item.modulo_id))
        .map((item) => item.id);

      const nextValue = normalizeConteudosData({
        ...prev,
        cursos: (prev.cursos || []).filter((item) => item.id !== cursoId),
        modulos: (prev.modulos || []).filter(
          (item) => item.curso_id !== cursoId,
        ),
        aulas: (prev.aulas || []).filter(
          (item) => !modulosIds.includes(item.modulo_id),
        ),
        progresso: Object.keys(prev.progresso || {}).reduce(
          (acc, studentKey) => {
            const currentProgress = { ...(prev.progresso?.[studentKey] || {}) };
            aulasIds.forEach((aulaId) => delete currentProgress[aulaId]);
            acc[studentKey] = currentProgress;
            return acc;
          },
          {},
        ),
      });

      previousRef.current = nextValue;
      return nextValue;
    });
  }, []);

  const restoreOlimpiadaCurso = useCallback(async (cursoId) => {
    await restoreOlimpiadaCursoApi(cursoId);

    const { snapshot } = await fetchOlimpiadaConteudoState();
    const normalized = normalizeConteudosData(snapshot);
    skipSyncRef.current = true;
    previousRef.current = normalized;
    setOlimpiadaConteudoRaw(normalized);
  }, []);

  const addOlimpiadaModulo = useCallback(async (payload) => {
    const modulo = await createOlimpiadaModulo(payload);
    if (!modulo) throw new Error("Falha ao criar modulo");

    setOlimpiadaConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        modulos: [...(prev.modulos || []), modulo],
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return modulo;
  }, []);

  const updateOlimpiadaModulo = useCallback(async (moduloId, payload) => {
    const modulo = await updateOlimpiadaModuloApi(moduloId, payload);
    if (!modulo) throw new Error("Falha ao atualizar modulo");

    setOlimpiadaConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        modulos: (prev.modulos || []).map((item) =>
          item.id === moduloId ? modulo : item,
        ),
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return modulo;
  }, []);

  const removeOlimpiadaModulo = useCallback(async (moduloId) => {
    await deleteOlimpiadaModulo(moduloId);

    setOlimpiadaConteudoRaw((prev) => {
      const aulasIds = (prev.aulas || [])
        .filter((item) => item.modulo_id === moduloId)
        .map((item) => item.id);

      const nextValue = normalizeConteudosData({
        ...prev,
        modulos: (prev.modulos || []).filter((item) => item.id !== moduloId),
        aulas: (prev.aulas || []).filter(
          (item) => item.modulo_id !== moduloId,
        ),
        progresso: Object.keys(prev.progresso || {}).reduce(
          (acc, studentKey) => {
            const currentProgress = { ...(prev.progresso?.[studentKey] || {}) };
            aulasIds.forEach((aulaId) => delete currentProgress[aulaId]);
            acc[studentKey] = currentProgress;
            return acc;
          },
          {},
        ),
      });

      previousRef.current = nextValue;
      return nextValue;
    });
  }, []);

  const restoreOlimpiadaModulo = useCallback(async (moduloId, payload = {}) => {
    await restoreOlimpiadaModuloApi(moduloId, payload);

    const { snapshot } = await fetchOlimpiadaConteudoState();
    const normalized = normalizeConteudosData(snapshot);
    skipSyncRef.current = true;
    previousRef.current = normalized;
    setOlimpiadaConteudoRaw(normalized);
  }, []);

  const addOlimpiadaAula = useCallback(async (payload) => {
    const aula = await createOlimpiadaAula(payload);
    if (!aula) throw new Error("Falha ao criar aula");

    setOlimpiadaConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        aulas: [...(prev.aulas || []), aula],
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return aula;
  }, []);

  const updateOlimpiadaAula = useCallback(async (aulaId, payload) => {
    const aula = await updateOlimpiadaAulaApi(aulaId, payload);
    if (!aula) throw new Error("Falha ao atualizar aula");

    setOlimpiadaConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        aulas: (prev.aulas || []).map((item) =>
          item.id === aulaId ? aula : item,
        ),
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return aula;
  }, []);

  const removeOlimpiadaAula = useCallback(async (aulaId) => {
    await deleteOlimpiadaAula(aulaId);

    setOlimpiadaConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        aulas: (prev.aulas || []).filter((item) => item.id !== aulaId),
        progresso: Object.keys(prev.progresso || {}).reduce(
          (acc, studentKey) => {
            const currentProgress = { ...(prev.progresso?.[studentKey] || {}) };
            delete currentProgress[aulaId];
            acc[studentKey] = currentProgress;
            return acc;
          },
          {},
        ),
      });
      previousRef.current = nextValue;
      return nextValue;
    });
  }, []);

  const restoreOlimpiadaAula = useCallback(async (aulaId, payload = {}) => {
    await restoreOlimpiadaAulaApi(aulaId, payload);

    const { snapshot } = await fetchOlimpiadaConteudoState();
    const normalized = normalizeConteudosData(snapshot);
    skipSyncRef.current = true;
    previousRef.current = normalized;
    setOlimpiadaConteudoRaw(normalized);
  }, []);

  const sessionKey = adminLogged
    ? "admin"
    : currentStudent
    ? String(currentStudent.id)
    : null;

  // Bootstrap do admin: envia estrutura se o servidor ainda não tem dados
  useEffect(() => {
    if (!adminLogged || !loadedRef.current) return;
    if (apiInitializedRef.current || bootstrapRef.current) return;

    const hasLocal =
      olimpiadaConteudo?.categorias?.length ||
      olimpiadaConteudo?.cursos?.length ||
      olimpiadaConteudo?.modulos?.length ||
      olimpiadaConteudo?.aulas?.length;

    if (!hasLocal) return;

    bootstrapRef.current = true;

    Promise.all([
      saveOlimpiadaConteudoStructure(olimpiadaConteudo),
      saveOlimpiadaConteudoErrors(olimpiadaConteudo?.erros || []),
    ])
      .then(() => {
        apiInitializedRef.current = true;
        previousRef.current = olimpiadaConteudo;
        bootstrapRef.current = false;
      })
      .catch(() => {
        bootstrapRef.current = false;
      });
  }, [adminLogged, olimpiadaConteudo]);

  // Carregamento/refresh ao entrar na tela
  useEffect(() => {
    const emOlimpiadaAluno = view === "olimpiada-conteudo";
    const emOlimpiadaAdmin = view === "admin" && adminAba === "olimpiada-conteudo-adm";

    if (!emOlimpiadaAluno && !emOlimpiadaAdmin) return;
    if (!sessionKey) return;

    const chave = `${sessionKey}:${view}:${adminAba}`;
    if (enterRefreshRef.current === chave) return;
    enterRefreshRef.current = chave;

    let active = true;

    if (emOlimpiadaAdmin) {
      // Admin carrega sempre estado completo do servidor
      fetchOlimpiadaConteudoState()
        .then(({ initialized, snapshot }) => {
          if (!active) return;
          apiInitializedRef.current = initialized;
          loadedRef.current = true;
          skipSyncRef.current = true;
          setOlimpiadaConteudoRaw(snapshot || EMPTY_STATE);
          previousRef.current = snapshot || EMPTY_STATE;
        })
        .catch(() => {
          loadedRef.current = true;
        });
    } else if (emOlimpiadaAluno && currentStudent) {
      // Aluno carrega snapshot leve
      if (!loadedRef.current) {
        fetchOlimpiadaConteudoAluno()
          .then(({ initialized, snapshot }) => {
            if (!active) return;
            apiInitializedRef.current = initialized;
            loadedRef.current = true;
            skipSyncRef.current = true;
            setOlimpiadaConteudoRaw(snapshot || EMPTY_STATE);
            previousRef.current = snapshot || EMPTY_STATE;
          })
          .catch(() => {
            loadedRef.current = true;
          });
      } else if (loadedRef.current) {
        // Refresh ao reentrar na tela
        fetchOlimpiadaConteudoAluno()
          .then(({ snapshot }) => {
            if (!active) return;
            setOlimpiadaConteudoRaw(snapshot || EMPTY_STATE);
            previousRef.current = snapshot || EMPTY_STATE;
          })
          .catch(() => {});
      }
    }

    return () => {
      active = false;
    };
  }, [adminAba, currentStudent, sessionKey, view]);

  // Sync automático ao mudar estado
  useEffect(() => {
    if (!loadedRef.current) return undefined;

    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      previousRef.current = olimpiadaConteudo;
      return undefined;
    }

    const previous = previousRef.current;
    const studentKey =
      currentStudent?.id !== undefined && currentStudent?.id !== null
        ? String(currentStudent.id)
        : null;

    const syncFn = async () => {
      try {
        if (adminLogged) {
          const tasks = [];

          const prevStructure = {
            categorias: previous?.categorias || [],
            cursos: previous?.cursos || [],
            modulos: previous?.modulos || [],
            aulas: previous?.aulas || [],
          };
          const nextStructure = {
            categorias: olimpiadaConteudo?.categorias || [],
            cursos: olimpiadaConteudo?.cursos || [],
            modulos: olimpiadaConteudo?.modulos || [],
            aulas: olimpiadaConteudo?.aulas || [],
          };

          if (serialize(prevStructure) !== serialize(nextStructure)) {
            tasks.push(saveOlimpiadaConteudoStructure(olimpiadaConteudo));
          }

          if (
            serialize(previous?.erros || []) !==
            serialize(olimpiadaConteudo?.erros || [])
          ) {
            tasks.push(saveOlimpiadaConteudoErrors(olimpiadaConteudo?.erros || []));
          }

          if (tasks.length) {
            await Promise.all(tasks);
            apiInitializedRef.current = true;
          }
        } else if (studentKey && apiInitializedRef.current) {
          const prevProgress = previous?.progresso?.[studentKey] || {};
          const nextProgress = olimpiadaConteudo?.progresso?.[studentKey] || {};

          if (serialize(prevProgress) !== serialize(nextProgress)) {
            await saveOlimpiadaConteudoProgress(nextProgress);
          }
        }

        previousRef.current = olimpiadaConteudo;
      } catch {
        // Mantém cache local como fallback
      }
    };

    window.clearTimeout(syncTimerRef.current);
    syncTimerRef.current = window.setTimeout(syncFn, 500);

    return () => {
      window.clearTimeout(syncTimerRef.current);
    };
  }, [adminLogged, currentStudent?.id, olimpiadaConteudo]);

  // Reset ao trocar de sessão
  useEffect(() => {
    loadedRef.current = false;
    apiInitializedRef.current = false;
    bootstrapRef.current = false;
    skipSyncRef.current = true;
    enterRefreshRef.current = "";
    setOlimpiadaConteudoRaw(EMPTY_STATE);
    previousRef.current = EMPTY_STATE;
  }, [sessionKey]);

  return {
    olimpiadaConteudo,
    setOlimpiadaConteudo,
    olimpiadaConteudoNavigationTarget,
    setOlimpiadaConteudoNavigationTarget,
    addOlimpiadaCategoria,
    updateOlimpiadaCategoria,
    removeOlimpiadaCategoria,
    restoreOlimpiadaCategoria,
    addOlimpiadaCurso,
    updateOlimpiadaCurso,
    removeOlimpiadaCurso,
    restoreOlimpiadaCurso,
    addOlimpiadaModulo,
    updateOlimpiadaModulo,
    removeOlimpiadaModulo,
    restoreOlimpiadaModulo,
    addOlimpiadaAula,
    updateOlimpiadaAula,
    removeOlimpiadaAula,
    restoreOlimpiadaAula,
  };
}
